import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const MS = 6000;
const isExternal = (u) => /^https?:\/\//i.test(u || "");

function BannerLink({ to, children, testId }) {
  if (!to) return <div className="absolute inset-0" data-testid={testId}>{children}</div>;
  if (isExternal(to)) return <a href={to} target="_blank" rel="noopener noreferrer" className="absolute inset-0 block" data-testid={testId}>{children}</a>;
  return <Link to={to} className="absolute inset-0 block" data-testid={testId}>{children}</Link>;
}

function Arrow({ dir, onClick }) {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button onClick={onClick} aria-label={dir < 0 ? "Anterior" : "Siguiente"} data-testid={dir < 0 ? "hero-slider-prev" : "hero-slider-next"}
      className={`absolute top-1/2 -translate-y-1/2 ${dir < 0 ? "left-3 sm:left-6" : "right-3 sm:right-6"} z-10 grid place-items-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#0a1b33]/40 backdrop-blur-md text-white border border-white/30 transition-[background-color,transform] duration-300 hover:scale-110 hover:bg-[#0a1b33]/70`}>
      <Icon size={20} />
    </button>
  );
}

function Progress({ count, i, paused, setI }) {
  return (
    <div className="absolute bottom-4 sm:bottom-7 left-4 sm:left-12 z-10 flex gap-3 sm:gap-5" data-testid="hero-slider-progress">
      {Array.from({ length: count }, (_, k) => (
        <button key={k} onClick={() => setI(k)} className="text-left w-12 sm:w-24" aria-label={`Banner ${k + 1}`} data-testid={`hero-slider-dot-${k}`}>
          <span className={`text-[0.65rem] sm:text-xs font-bold tracking-[0.2em] drop-shadow ${k === i ? "text-white" : "text-white/55"}`}>0{k + 1}</span>
          <span className="block h-[3px] mt-1.5 sm:mt-2 bg-white/30 overflow-hidden rounded-full">
            {k === i && <motion.span key={`${i}-${paused}`} className="block h-full bg-[#e6c364]" initial={{ width: "0%" }} animate={{ width: paused ? "0%" : "100%" }} transition={{ duration: paused ? 0 : MS / 1000, ease: "linear" }} />}
          </span>
        </button>
      ))}
    </div>
  );
}

function Fallback() {
  return (
    <section className="bg-[#0f2544] text-white" data-testid="home-hero">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 lg:px-12 py-24 md:py-36">
        <h1 className="font-serif font-semibold text-5xl sm:text-6xl xl:text-[5.6rem] leading-[0.92] tracking-tight max-w-4xl">Libros que edifican <span className="text-[#e6c364]">vidas</span></h1>
        <p className="text-white/70 text-lg max-w-xl mt-6">Publicamos literatura cristiana, obras con propósito e historias que perduran y transforman.</p>
        <Link to="/libros" className="group inline-flex items-center gap-3 mt-9 rounded-full bg-white px-7 py-4 text-xs font-semibold tracking-[0.16em] uppercase text-[#0f2544] hover:bg-[#e6c364] transition-colors" data-testid="hero-descubre-libros">
          Descubre nuestros libros <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  );
}

export default function HomeHero({ data }) {
  const banners = data?.banners || [];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = banners.length;

  useEffect(() => {
    if (paused || n < 2) { return undefined; }
    const t = setTimeout(() => setI((x) => (x + 1) % n), MS);
    return () => clearTimeout(t);
  }, [i, paused, n]);

  useEffect(() => {
    const mob = window.innerWidth < 640 && banners.every((x) => x.imagen_movil);
    banners.forEach((x) => { const im = new Image(); im.src = mob ? x.imagen_movil : x.imagen; });
  }, [banners]);

  const hover = (v) => (e) => { if (e.pointerType === "mouse") setPaused(v); };

  if (!data) return <section className="aspect-[4/5] sm:aspect-[12/5] sm:max-h-[85vh] w-full bg-[#0a1b33] animate-pulse" data-testid="home-hero" />;
  if (n === 0) return <Fallback />;
  const b = banners[i % n];
  const allMobile = banners.every((x) => x.imagen_movil);

  return (
    <section className={`relative overflow-hidden ${allMobile ? "aspect-[4/5]" : "aspect-[12/5]"} sm:aspect-[12/5] sm:max-h-[85vh] w-full bg-[#0a1b33]`} onPointerEnter={hover(true)} onPointerLeave={hover(false)} data-testid="home-hero">
      <h1 className="sr-only">Editorial Harvest · Libros que edifican vidas</h1>
      <AnimatePresence initial={false}>
        <motion.div key={i} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }}>
          <BannerLink to={b.enlace} testId={`hero-banner-${i}`}>
            <picture>
              {allMobile && <source media="(max-width: 639px)" srcSet={b.imagen_movil} />}
              <motion.img src={b.imagen} alt={b.alt || "Editorial Harvest"} fetchpriority={i === 0 ? "high" : "auto"} className="w-full h-full object-cover" initial={{ scale: 1 }} animate={{ scale: 1.06 }} transition={{ duration: MS / 1000 + 1.2, ease: "linear" }} />
            </picture>
          </BannerLink>
        </motion.div>
      </AnimatePresence>
      {n > 1 && (
        <>
          <Arrow dir={-1} onClick={() => setI((i - 1 + n) % n)} />
          <Arrow dir={1} onClick={() => setI((i + 1) % n)} />
          <Progress count={n} i={i % n} paused={paused} setI={setI} />
        </>
      )}
    </section>
  );
}
